import { User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { useLocaleDateString } from '@/shared/hooks/useLocaleDateString';
import type { EmployeeInfo } from '../../constants';

interface ActivityRowProps {
  author: EmployeeInfo | undefined;
  showAuthor: boolean;
  actionLabel: string;
  date: string | Date | undefined;
  formatDate: (date: string | Date) => string;
  showConnector?: boolean;
}

function ActivityRow({
  author,
  showAuthor,
  actionLabel,
  date,
  formatDate,
  showConnector,
}: ActivityRowProps) {
  return (
    <div
      className={`relative flex items-start gap-2.5${
        showConnector ? ' pb-4' : ''
      }`}
    >
      <div className="relative z-[1] flex-shrink-0">
        {showAuthor && author ? (
          <UserSelectorAvatar
            fullName={author.fullName}
            imageUrl={author.imageUrl}
            size="xs"
            variant="neutral"
          />
        ) : (
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200">
            <User className="h-3 w-3 text-muted-foreground" />
          </div>
        )}
      </div>
      {showConnector && (
        <div className="absolute start-2.5 top-6 bottom-0 w-0.5 -translate-x-1/2 rounded-full bg-border rtl:translate-x-1/2" />
      )}
      <div className="flex flex-1 flex-wrap items-baseline gap-1.5 pt-0.5 text-[13px] text-muted-foreground">
        {showAuthor && author && (
          <span className="font-medium text-foreground/80">
            {author.fullName}
          </span>
        )}
        <span>{actionLabel}</span>
        {date && (
          <span className="text-xs text-muted-foreground/60">
            &middot; {formatDate(date)}
          </span>
        )}
      </div>
    </div>
  );
}

interface SignalActivityTimelineProps {
  author: EmployeeInfo | undefined;
  isAnonymous: boolean;
  isCreator: boolean;
  createdAt: string | Date | undefined;
  lastUpdatedAt: string | Date | undefined;
}

export function SignalActivityTimeline({
  author,
  isAnonymous,
  isCreator,
  createdAt,
  lastUpdatedAt,
}: SignalActivityTimelineProps) {
  const { t } = useTranslation('knowledge');
  const { toLocaleDateString } = useLocaleDateString();

  const formatDate = (date: string | Date) =>
    toLocaleDateString(date.toString(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const showUpdatedEntry = lastUpdatedAt && lastUpdatedAt !== createdAt;
  const showAuthor = !isAnonymous || isCreator;

  return (
    <div className="mt-auto border-t pt-6 pb-4">
      <h3 className="mb-4 text-sm font-semibold text-foreground">
        {t('detailActivity')}
      </h3>
      <div className="relative">
        {showUpdatedEntry && (
          <ActivityRow
            author={author}
            showAuthor={showAuthor}
            actionLabel={t('detailUpdatedSignal')}
            date={lastUpdatedAt}
            formatDate={formatDate}
            showConnector
          />
        )}
        <ActivityRow
          author={author}
          showAuthor={showAuthor}
          actionLabel={t('detailCreatedSignal')}
          date={createdAt}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
}
